'use client';

import { useCallback, useEffect, useRef } from 'react';
import { isInApp } from '@/libs/comeAppSDK';
import loadingBg from '@/assets/images/common/loading_bg.jpg';
import { usePathname } from 'next/navigation';

/**
 * Preloader 组件 - 统一的预加载器实现
 * 
 * 设计原则：
 * - 客户端组件，在服务端渲染时输出 HTML 结构，确保静态渲染包含预加载器
 * - 所有逻辑统一在客户端执行，避免重复
 * - 使用 CSS 隐藏和 pointer-events，保持 DOM 结构稳定
 */

export function Preloader() {
  const preloaderRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  // 获取当前路由信息
  const pathname = usePathname();

  /**
   * 检查是否在允许显示非强制弹窗的路由
   * 允许的路由：首页（/en、/en?menuTab=Explore、/en?menuTab=Casino）
   */
  const isShowPreloader = useCallback(() => {
    // 移除语言前缀，统一处理路由（如 /en/ -> /）
    const pathWithoutLocale = pathname.replace(/^\/(en|pt|hi|id|es|tr|vi)/, '') || '/';
    // 大奖公示页不需要动画
    if (['/fantasy-record-single'].includes(pathWithoutLocale)) {
      return false;
    }

    return true;
  }, [pathname]);

  useEffect(() => {
    const preloaderEl = preloaderRef.current;
    const videoEl = videoRef.current;

    if (!preloaderEl || !videoEl) return;

    let isHidden = false; // 防止重复隐藏
    const MAX_PLAY_MS = 2000;
    let hideTimer: number | null = null;
    let cleanupLoadListener: (() => void) | null = null;

    // 检查 sessionStorage 中是否已经播放过视频
    const hasVideoPlayed = () => {
      try {
        return sessionStorage.getItem('preloader-video-played') === 'true';
      } catch (e) {
        console.log(e);
        return false;
      }
    };

    // 标记视频已播放
    const markVideoPlayed = () => {
      try {
        sessionStorage.setItem('preloader-video-played', 'true');
      } catch (e) {
        // 忽略 sessionStorage 错误
        console.log(e);
      }
    };

    // 隐藏整个 preloader 的函数
    const hidePreloader = () => {
      if (isHidden) return;
      isHidden = true;

      if (hideTimer !== null) {
        window.clearTimeout(hideTimer);
        hideTimer = null;
      }
      if (cleanupLoadListener) {
        cleanupLoadListener();
        cleanupLoadListener = null;
      }

      preloaderEl.style.opacity = '0';
      preloaderEl.style.transition = 'opacity 0.3s ease-out';
      preloaderEl.style.pointerEvents = 'none';

      setTimeout(() => {
        preloaderEl.style.display = 'none';
        document.body.classList.remove('overflow-hidden');
      }, 300);
    };

    // 如果已经播放过视频，直接隐藏整个 preloader
    if (hasVideoPlayed()) {
      videoEl.style.display = 'none';
      hidePreloader();
      return;
    }

    // 检查是否在 App 中，只有在非 App 环境下才显示和处理视频
    let shouldShowVideo = true;
    try {
      const inApp = isInApp();
      if (inApp) {
        // 在 App 中，隐藏视频并直接隐藏 preloader
        videoEl.style.display = 'none';
        shouldShowVideo = false;
      }
    } catch (e) {
      // 如果 isInApp 检查失败，默认显示视频（非 App 环境）
      shouldShowVideo = true;
      console.log(e);
    }

    // 如果应该在非 App 环境下显示，则显示视频并初始化
    if (shouldShowVideo) {
      videoEl.style.display = '';

      // 最长播放 MAX_PLAY_MS，超时强制隐藏（防止 load 未触发或资源卡住）
      hideTimer = window.setTimeout(() => {
        hidePreloader();
      }, MAX_PLAY_MS);

      // 监听视频开始播放，标记为已播放
      const handlePlay = () => {
        markVideoPlayed();
      };
      videoEl.addEventListener('play', handlePlay, { once: true });

      // 页面加载完成后隐藏 preloader
      const handleLoad = () => {
        // 确保 React 完全 hydrated 后再隐藏
        requestAnimationFrame(() => {
          hidePreloader();
        });
      };

      // 如果页面已经加载完成
      if (document.readyState === 'complete') {
        handleLoad();
      } else {
        window.addEventListener('load', handleLoad, { once: true });
        cleanupLoadListener = () => window.removeEventListener('load', handleLoad);
      }
    } else {
      // 在 App 中，直接隐藏 preloader
      hidePreloader();
      return;
    }

    return () => {
      if (hideTimer !== null) {
        window.clearTimeout(hideTimer);
        hideTimer = null;
      }
      if (cleanupLoadListener) {
        cleanupLoadListener();
        cleanupLoadListener = null;
      }
    };
  }, []);

  if (!isShowPreloader()) {
    return null;
  }

  return (
    <div
      id="preloader"
      ref={preloaderRef}
      className="fixed inset-0 z-[9999]"
      style={{
        pointerEvents: 'none',
        backgroundImage: `url(${loadingBg.src})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <video
        id="preloader-video"
        ref={videoRef}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[218px] h-[133px] object-cover pointer-events-none"
        style={{ display: 'none' }}
        autoPlay
        loop
        muted
        playsInline
        disablePictureInPicture
        controls={false}
      >
        <source src="/assets/video/loading_v3.mp4" type="video/mp4" />
      </video>
    </div>
  );
}

